import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, Image, ActivityIndicator, Modal } from 'react-native';
import { getFirestore, doc, getDoc, updateDoc, deleteDoc, collection, query, where, limit, getDocs, addDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import * as DocumentPicker from 'expo-document-picker';
import { Ionicons } from '@expo/vector-icons';
import { useUser } from '../components/LoginPersistence';
import Constants from 'expo-constants';

// Member List Item Component
const MemberListItem = ({ memberId, org, userData, colors, onKickMember, onTransferOwnership }) => {
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);
  const db = getFirestore();

  useEffect(() => {
    const loadMember = async () => {
      try {
        const memberDoc = await getDoc(doc(db, 'users', memberId));
        if (memberDoc.exists()) {
          setMemberData(memberDoc.data());
        }
      } catch (error) {
        console.error('Error loading member:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMember();
  }, [memberId]);

  if (loading) {
    return (
      <View style={{
        padding: 8,
        backgroundColor: '#fff',
        borderRadius: 5,
        marginBottom: 5
      }}>
        <Text style={{ fontSize: 14, color: '#999' }}>Loading...</Text>
      </View>
    );
  }

  const isOwner = memberId === org.ownerId;
  const isCurrentUserOwner = userData?.uid === org.ownerId;

  return (
    <View style={{
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 8,
      backgroundColor: '#fff',
      borderRadius: 5,
      marginBottom: 5
    }}>
      <Text style={{ fontSize: 14 }}>
        {memberData?.username || memberData?.email || 'Unknown User'}
        {isOwner && <Text style={{ color: colors.purpleReplacement }}> (Owner)</Text>}
      </Text>
      {isCurrentUserOwner && !isOwner && (
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <TouchableOpacity
            style={{
              backgroundColor: '#e74c3c',
              padding: 6,
              borderRadius: 4,
              paddingHorizontal: 10
            }}
            onPress={() => onKickMember(org.id, memberId)}
          >
            <Text style={{ color: '#fff', fontSize: 12 }}>Remove</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={{
              backgroundColor: colors.purpleReplacement,
              padding: 6,
              borderRadius: 4,
              paddingHorizontal: 10
            }}
            onPress={() => onTransferOwnership(org.id, memberId)}
          >
            <Text style={{ color: '#fff', fontSize: 12 }}>Transfer Ownership</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

// Helper function to get file type
const getFileType = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
  if (extension === 'pdf') return 'pdf';
  if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(extension)) return 'audio';
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) return 'video';
  return 'file';
};

export const OrganizationCard = ({
  orgId,
  orgData,
  colors,
  profileStyles,
  onSave,
  onClose,
  onInvite,
  isEditable = true
}) => {
  const { user: userData } = useUser();
  const db = getFirestore();
  const [orgMedia, setOrgMedia] = useState({ media: orgData?.media || [] });
  const [bioHeight, setBioHeight] = useState(100);
  const [inviteEmailInput, setInviteEmailInput] = useState('');
  const [orgPhotoURL, setOrgPhotoURL] = useState(orgData?.photoURL || null);
  const [orgUsername, setOrgUsername] = useState(orgData?.username || '');
  const [orgTitle, setOrgTitle] = useState(orgData?.title || '');
  const [orgLocation, setOrgLocation] = useState(orgData?.location || '');
  const [orgBio, setOrgBio] = useState(orgData?.bio || '');
  const [isHovering, setIsHovering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublic, setIsPublic] = useState(orgData?.isPublic ?? false);
  const [members, setMembers] = useState(orgData?.members || []);
  const [orgOwnerId, setOrgOwnerId] = useState(orgData?.ownerId || null);
  const [isInviting, setIsInviting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(null);
  const [invitedEmails, setInvitedEmails] = useState(orgData?.invitedEmails || []);
  const isOwner = userData?.uid === orgOwnerId;

  const defaultProfilePhoto = require('../../assets/profile_photos/koi.png');

  // Load organization data when orgId changes
  useEffect(() => {
    if (orgData) {
      setOrgUsername(orgData.username || '');
      setOrgTitle(orgData.title || '');
      setOrgLocation(orgData.location || '');
      setOrgBio(orgData.bio || '');
      setOrgPhotoURL(orgData.photoURL || null);
      setOrgMedia({ media: orgData.media || [] });
      setIsPublic(orgData.isPublic ?? false);
      setMembers(orgData.members || []);
      setOrgOwnerId(orgData.ownerId || null);
      setInvitedEmails(orgData.invitedEmails || []);
    }
  }, [orgData]);

  // Reload organization data periodically to get updated members
  useEffect(() => {
    if (orgId) {
      const loadOrgData = async () => {
        console.log('Loading organization data for:', orgId);
        try {
          const orgDoc = await getDoc(doc(db, 'organizations', orgId));
          if (orgDoc.exists()) {
            const data = orgDoc.data();
            setMembers(data.members || []);
            setOrgOwnerId(data.ownerId || null);
          }
        } catch (error) {
          console.error('Error loading organization data:', error);
        }
      };
      loadOrgData();
    }
  }, [orgId]);

  const handleOrgPhotoUpload = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({ type: 'image/*' });
      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        const fileUri = file.uri;
        const response = await fetch(fileUri);
        const fileBlob = await response.blob();

        const storage = getStorage();
        const fileExt = file.name.split('.').pop();
        const fileName = `org_${Date.now()}.${fileExt}`;
        const storageRef = ref(storage, `organizationPhotos/${fileName}`);

        await uploadBytes(storageRef, fileBlob);
        const photoURL = await getDownloadURL(storageRef);
        
        // Update state
        setOrgPhotoURL(photoURL);
        
        // Save to organization document immediately
        if (orgId) {
          try {
            await updateDoc(doc(db, 'organizations', orgId), {
              photoURL: photoURL,
              updatedAt: serverTimestamp()
            });
            
            // Call handleSave to update other fields if needed, or just refresh
            if (onSave) {
              onSave();
            }
          } catch (error) {
            console.error('Error saving photo URL:', error);
            alert('Photo uploaded but failed to save. Please try again.');
          }
        }

      }
    } catch (error) {
      console.error('Error uploading organization photo:', error);
      alert('Failed to update organization photo. Please try again.');
    }
  };

  // Helper function to extract storage path from download URL
  const extractStoragePathFromUrl = (downloadUrl) => {
    try {
      // Firebase Storage URLs format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token={token}
      const url = new URL(downloadUrl);
      const pathMatch = url.pathname.match(/\/o\/(.+)/);
      
      if (pathMatch) {
        const encodedPath = pathMatch[1];
        const filePath = decodeURIComponent(encodedPath);
        return filePath;
      }
      return null;
    } catch (error) {
      console.error('Error extracting path from URL:', error);
      return null;
    }
  };

  const handleSave = async () => {
    if (!orgId) return;

    // Only owners can save
    if (!isOwner) {
      return;
    }

    // Only save if username and title are provided
    if (!orgUsername.trim() || !orgTitle.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      const updateData = {
        username: orgUsername.trim(),
        title: orgTitle.trim(),
        location: orgLocation.trim() || '',
        bio: orgBio.trim() || '',
        photoURL: orgPhotoURL || null,
        media: orgMedia.media || [],
        updatedAt: serverTimestamp()
      };

      // Set isPublic to true if username and title are provided
      if (orgUsername.trim() && orgTitle.trim()) {
        updateData.isPublic = true;
        setIsPublic(true);
      }

      await updateDoc(doc(db, 'organizations', orgId), updateData);
      
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving organization:', error);
      alert('Failed to save organization. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const OrganizationPhotoContainer = () => (
    <TouchableOpacity
      style={profileStyles.profileImageContainer}
      onPress={handleOrgPhotoUpload}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Image
        source={orgPhotoURL ? { uri: orgPhotoURL } : defaultProfilePhoto}
        style={profileStyles.profileImage}
        resizeMode="cover"
      />
      <View style={[
        profileStyles.profileImageOverlay,
        isHovering && profileStyles.profileImageOverlayHovered
      ]}>
        <Text style={profileStyles.profileImageOverlayText}>
          Upload Profile Photo
        </Text>
      </View>
    </TouchableOpacity>
  );

  const handleOrgMediaUpload = async (mediaType) => {
    try {
      const result = await DocumentPicker.getDocumentAsync({ 
        type: '*/*',
        copyToCacheDirectory: true
      });
      
      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        const fileUri = file.uri;
        const response = await fetch(fileUri);
        const fileBlob = await response.blob();
        
        const storage = getStorage();
        const mediaIndex = parseInt(mediaType.replace('media', '')) - 1;
        const storageRef = ref(storage, `organizations/${userData.uid || 'temp'}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, fileBlob);
        const downloadURL = await getDownloadURL(storageRef);

        const newMedia = [...(orgMedia.media || [])];
        while (newMedia.length <= mediaIndex) {
          newMedia.push(null);
        }
        newMedia[mediaIndex] = {
          id: Date.now().toString(),
          name: file.name,
          url: downloadURL
        };

        setOrgMedia({ media: newMedia });
        
        // Save if organization exists and required fields are filled
        if (orgId && orgUsername.trim() && orgTitle.trim()) {
          try {
            await updateDoc(doc(db, 'organizations', orgId), {
              media: newMedia,
              updatedAt: serverTimestamp()
            });
          } catch (error) {
            console.error('Error saving media:', error);
            alert('Failed to save media. Please try again.');
          }
        }
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      alert('Failed to upload media. Please try again.');
    }
  };

  const handleOrgMediaDelete = async (mediaId) => {
    try {
      const mediaItem = orgMedia.media.find(m => m && m.id === mediaId);
      if (mediaItem && mediaItem.url) {
        try {
          const storage = getStorage();
          const mediaPath = extractStoragePathFromUrl(mediaItem.url);
          if (mediaPath) {
            const storageRef = ref(storage, mediaPath);
            await deleteObject(storageRef);
          }
        } catch (error) {
          console.error('Error deleting from storage:', error);
          // Continue with deletion even if storage deletion fails
        }
      }

      const newMedia = orgMedia.media.map(m => m && m.id === mediaId ? null : m);
      setOrgMedia({ media: newMedia });
      
      // Save if organization exists and required fields are filled
      if (orgId && orgUsername.trim() && orgTitle.trim()) {
        handleSave();
      }
    } catch (error) {
      console.error('Error deleting media:', error);
    }
  };

  const handleInviteFromCard = async () => {
    if (!inviteEmailInput.trim()) {
      setShowErrorModal('Please enter an email address');
      return;
    }

    if (!orgId) {
      setShowErrorModal('Organization not created yet.');
      return;
    }

    if (!isOwner) {
      setShowErrorModal('Only the owner can invite members');
      return;
    }

    setIsInviting(true);

    try {
      // Check max members per organization
      const maxMembersPerOrg = parseInt(Constants.expoConfig?.extra?.maxMembersPerOrg || process.env.EXPO_PUBLIC_MAX_MEMBERS_PER_ORG || '0', 10);
      if (maxMembersPerOrg > 0 && members.length >= maxMembersPerOrg) {
        setShowErrorModal(`This organization has reached the maximum number of members (${maxMembersPerOrg}). You cannot invite more members at this time.`);
        setIsInviting(false);
        return;
      }

      // Check if user exists
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', inviteEmailInput.trim().toLowerCase()),
        limit(1)
      );
      const userSnapshot = await getDocs(usersQuery);
      
      if (userSnapshot.empty) {
        setShowErrorModal('User with this email not found');
        setIsInviting(false);
        return;
      }

      const inviteeUserId = userSnapshot.docs[0].id;
      const emailLower = inviteEmailInput.trim().toLowerCase();

      // Check if already a member
      if (members.includes(inviteeUserId)) {
        setShowErrorModal('User is already a member of this organization');
        setIsInviting(false);
        return;
      }

      // Check if there's already a pending invitation in the database
      const existingInvitationsQuery = query(
        collection(db, 'organization_invitations'),
        where('organizationId', '==', orgId),
        where('inviteeEmail', '==', emailLower),
        where('status', '==', 'pending'),
        limit(1)
      );
      const existingInvitationsSnapshot = await getDocs(existingInvitationsQuery);
      
      if (!existingInvitationsSnapshot.empty) {
        setShowErrorModal('This user has already been invited. Please wait for them to respond.');
        setIsInviting(false);
        return;
      }

      // Also check local state as a secondary check
      if (invitedEmails.some(inv => inv.email === emailLower && inv.status === 'pending')) {
        setShowErrorModal('This user has already been invited. Please wait for them to respond.');
        setIsInviting(false);
        return;
      }

      // Get organization name
      const orgName = orgUsername.trim() || orgTitle.trim() || 'Organization';

      // Create invitation
      await addDoc(collection(db, 'organization_invitations'), {
        organizationId: orgId,
        organizationName: orgName,
        inviterId: userData.uid,
        inviterEmail: userData.email,
        inviteeId: inviteeUserId,
        inviteeEmail: emailLower,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      // Track invited email
      const newInvitedEmail = {
        email: emailLower,
        invitedAt: new Date().toISOString(),
        status: 'pending',
        inviterId: userData.uid,
        inviterEmail: userData.email
      };
      const updatedInvitedEmails = [...invitedEmails, newInvitedEmail];
      setInvitedEmails(updatedInvitedEmails);

      // Update organization document with invited emails
      try {
        await updateDoc(doc(db, 'organizations', orgId), {
          invitedEmails: updatedInvitedEmails,
          updatedAt: serverTimestamp()
        });
      } catch (updateError) {
        console.error('Error updating invited emails:', updateError);
      }

      // Send email to the invited user
      try {
        const functions = getFunctions();
        const handleEmail = httpsCallable(functions, 'handleEmail');
        
        // Get inviter name from userData
        const inviterName = userData.username || userData.displayName || userData.email || 'Someone';
        
        await handleEmail({
          template: 'organization_invitation',
          recipient: emailLower,
          emailData: {
            organizationName: orgName,
            inviterName: inviterName,
            inviterEmail: userData.email
          }
        });
      } catch (emailError) {
        console.error('Error sending invitation email:', emailError);
        // Don't fail the invitation if email fails, just log it
      }

      setInviteEmailInput('');
      setShowSuccessModal(`Invitation sent successfully to ${emailLower}!`);
      setIsInviting(false);
      
      if (onInvite) {
        onInvite();
      }
    } catch (error) {
      console.error('Error inviting member:', error);
      setShowErrorModal(`Failed to send invitation. Please try again.\n\nError: ${error.message}`);
      setIsInviting(false);
    }
  };

  const handleKickMember = async (orgId, memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      const orgRef = doc(db, 'organizations', orgId);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists() || orgDoc.data().ownerId !== userData.uid) {
        alert('You do not have permission to remove members');
        return;
      }

      const orgData = orgDoc.data();
      const updatedMembers = orgData.members.filter(m => m !== memberId);

      await updateDoc(orgRef, {
        members: updatedMembers,
        updatedAt: serverTimestamp()
      });

      // Remove organization from user's organizations field
      const memberUserRef = doc(db, 'users', memberId);
      const memberUserDoc = await getDoc(memberUserRef);
      if (memberUserDoc.exists()) {
        const memberUserData = memberUserDoc.data();
        const currentOrganizations = memberUserData.organizations || {};
        const { [orgId]: removed, ...remainingOrganizations } = currentOrganizations;
        
        await updateDoc(memberUserRef, {
          organizations: remainingOrganizations
        });
      }

      setMembers(updatedMembers);
      alert('Member removed successfully');
      
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member. Please try again.');
    }
  };

  const handleTransferOwnership = async (orgId, newOwnerId) => {
    if (!window.confirm('Are you sure you want to transfer ownership? You will no longer be the owner.')) {
      return;
    }

    try {
      const orgRef = doc(db, 'organizations', orgId);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists() || orgDoc.data().ownerId !== userData.uid) {
        alert('You do not have permission to transfer ownership');
        return;
      }

      // Get new owner email
      const newOwnerDoc = await getDoc(doc(db, 'users', newOwnerId));
      const newOwnerEmail = newOwnerDoc.exists() ? newOwnerDoc.data().email : '';

      await updateDoc(orgRef, {
        ownerId: newOwnerId,
        ownerEmail: newOwnerEmail,
        updatedAt: serverTimestamp()
      });

      // Update organizations field for both old and new owner
      const oldOwnerRef = doc(db, 'users', userData.uid);
      const oldOwnerDoc = await getDoc(oldOwnerRef);
      if (oldOwnerDoc.exists()) {
        const oldOwnerData = oldOwnerDoc.data();
        const oldOwnerOrganizations = oldOwnerData.organizations || {};
        await updateDoc(oldOwnerRef, {
          organizations: {
            ...oldOwnerOrganizations,
            [orgId]: {
              role: 'member',
              joinedAt: oldOwnerOrganizations[orgId]?.joinedAt || serverTimestamp()
            }
          }
        });
      }

      const newOwnerRef = doc(db, 'users', newOwnerId);
      const newOwnerDocData = newOwnerDoc.exists() ? newOwnerDoc.data() : {};
      const newOwnerOrganizations = newOwnerDocData.organizations || {};
      await updateDoc(newOwnerRef, {
        organizations: {
          ...newOwnerOrganizations,
          [orgId]: {
            role: 'owner',
            joinedAt: newOwnerOrganizations[orgId]?.joinedAt || serverTimestamp()
          }
        }
      });

      setOrgOwnerId(newOwnerId);
      alert('Ownership transferred successfully');
      
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error transferring ownership:', error);
      alert('Failed to transfer ownership. Please try again.');
    }
  };

  const handleDeleteOrganization = async () => {
    if (!window.confirm('Are you sure you want to delete this organization? This action cannot be undone. All members will be removed and all data will be permanently deleted.')) {
      return;
    }

    if (!isOwner) {
      alert('Only the owner can delete the organization');
      return;
    }

    try {
      const orgRef = doc(db, 'organizations', orgId);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists() || orgDoc.data().ownerId !== userData.uid) {
        alert('You do not have permission to delete this organization');
        return;
      }

      const orgData = orgDoc.data();
      const storage = getStorage();

      // Delete organization photo from storage if it exists
      if (orgData.photoURL) {
        try {
          const photoPath = extractStoragePathFromUrl(orgData.photoURL);
          if (photoPath) {
            const photoRef = ref(storage, photoPath);
            await deleteObject(photoRef);
          }
        } catch (error) {
          console.error('Error deleting organization photo from storage:', error);
          // Continue with deletion even if photo deletion fails
        }
      }

      // Delete organization media from storage if they exist
      if (orgData.media && Array.isArray(orgData.media)) {
        for (const mediaItem of orgData.media) {
          if (mediaItem && mediaItem.url) {
            try {
              const mediaPath = extractStoragePathFromUrl(mediaItem.url);
              if (mediaPath) {
                const mediaRef = ref(storage, mediaPath);
                await deleteObject(mediaRef);
              }
            } catch (error) {
              console.error('Error deleting media from storage:', error);
              // Continue with deletion even if media deletion fails
            }
          }
        }
      }

      // Remove organization from all members' organizations field
      const allMemberIds = [...(orgData.members || []), orgData.ownerId].filter(Boolean);
      const uniqueMemberIds = [...new Set(allMemberIds)]; // Remove duplicates
      
      // Use batch writes for efficiency
      let batch = writeBatch(db);
      let batchCount = 0;
      const batchSize = 500; // Firestore batch limit
      
      for (const memberId of uniqueMemberIds) {
        try {
          const memberUserRef = doc(db, 'users', memberId);
          const memberUserDoc = await getDoc(memberUserRef);
          
          if (memberUserDoc.exists()) {
            const memberUserData = memberUserDoc.data();
            const currentOrganizations = memberUserData.organizations || {};
            
            // Remove this organization from the user's organizations field
            const { [orgId]: removed, ...remainingOrganizations } = currentOrganizations;
            
            batch.update(memberUserRef, {
              organizations: remainingOrganizations
            });
            
            batchCount++;
            
            // Commit batch if we reach the limit
            if (batchCount >= batchSize) {
              await batch.commit();
              batch = writeBatch(db);
              batchCount = 0;
            }
          }
        } catch (error) {
          console.error(`Error updating user ${memberId} organizations field:`, error);
          // Continue with other members even if one fails
        }
      }
      
      // Commit any remaining updates
      if (batchCount > 0) {
        await batch.commit();
      }

      // Delete the organization document
      await deleteDoc(orgRef);

      alert('Organization deleted successfully');
      
      // Call onSave to refresh the organizations list
      if (onSave) {
        onSave();
      }
      
      // If there's an onClose callback (for create card), call it
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      alert('Failed to delete organization. Please try again.');
    }
  };

  // Determine border color based on isPublic status
  const borderColor = isPublic ? '#0077be' : '#ff3b30'; // Blue when public, red when private

  return (
    <View style={[profileStyles.profileTopLeftCard, {
      backgroundColor: '#FFE99C',
      borderWidth: 3,
      borderColor: borderColor,
      borderStyle: 'solid',
      marginBottom: 20
    }]}>
      {isOwner ? (
        <OrganizationPhotoContainer />
      ) : (
        <View style={profileStyles.profileImageContainer}>
          <Image
            source={orgPhotoURL ? { uri: orgPhotoURL } : defaultProfilePhoto}
            style={profileStyles.profileImage}
            resizeMode="cover"
          />
        </View>
      )}
      
      {/* Username */}
      <View style={{ width: '100%', marginTop: 20 }}>
        <TextInput
          style={[
            profileStyles.profileInput,
            {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              color: '#000000',
              height: 40,
              paddingTop: 12,
              paddingBottom: 12,
              paddingLeft: 12,
              borderRadius: 8,
              marginBottom: 10,
              opacity: isOwner ? 1 : 0.7
            }
          ]}
          value={orgUsername}
          placeholder="Organization Username *"
          placeholderTextColor="rgba(0, 0, 0, 0.5)"
          onChangeText={setOrgUsername}
          onBlur={handleSave}
          maxLength={100}
          editable={isOwner}
        />
        <Text style={[profileStyles.characterCount, {
          alignSelf: 'flex-end',
          marginBottom: 10,
          color: 'rgba(0, 0, 0, 0.5)',
          fontSize: 12
        }]}>
          {(orgUsername?.length || 0)}/100
        </Text>
      </View>

      {/* Title */}
      <View style={{ width: '100%' }}>
        <TextInput
          style={[
            profileStyles.profileInput,
            {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              color: '#000000',
              height: 40,
              paddingTop: 12,
              paddingBottom: 12,
              paddingLeft: 12,
              borderRadius: 8,
              marginBottom: 10,
              opacity: isOwner ? 1 : 0.7
            }
          ]}
          value={orgTitle}
          placeholder="Organization Title *"
          placeholderTextColor="rgba(0, 0, 0, 0.5)"
          onChangeText={setOrgTitle}
          onBlur={handleSave}
          maxLength={100}
          editable={isOwner}
        />
        <Text style={[profileStyles.characterCount, {
          alignSelf: 'flex-end',
          marginBottom: 10,
          color: 'rgba(0, 0, 0, 0.5)',
          fontSize: 12
        }]}>
          {(orgTitle?.length || 0)}/100
        </Text>
      </View>

      {/* Location */}
      <View style={{ width: '100%' }}>
        <TextInput
          style={[
            profileStyles.profileInput,
            {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              color: '#000000',
              height: 40,
              paddingTop: 12,
              paddingBottom: 12,
              paddingLeft: 12,
              borderRadius: 8,
              marginBottom: 10,
              opacity: isOwner ? 1 : 0.7
            }
          ]}
          value={orgLocation}
          placeholder="Organization Location"
          placeholderTextColor="rgba(0, 0, 0, 0.5)"
          onChangeText={setOrgLocation}
          onBlur={handleSave}
          maxLength={100}
          editable={isOwner}
        />
        <Text style={[profileStyles.characterCount, {
          alignSelf: 'flex-end',
          marginBottom: 10,
          color: 'rgba(0, 0, 0, 0.5)',
          fontSize: 12
        }]}>
          {(orgLocation?.length || 0)}/100
        </Text>
      </View>

      {/* Bio */}
      <View style={{ width: '100%' }}>
        <TextInput
          style={[
            profileStyles.profileInput,
            {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              color: '#000000',
              height: Math.max(100, bioHeight),
              textAlignVertical: 'top',
              paddingTop: 12,
              paddingBottom: 12,
              paddingLeft: 12,
              borderRadius: 8,
              opacity: isOwner ? 1 : 0.7
            }
          ]}
          value={orgBio}
          placeholder="Organization Bio"
          placeholderTextColor="rgba(0, 0, 0, 0.5)"
          onChangeText={setOrgBio}
          onContentSizeChange={(event) => {
            setBioHeight(event.nativeEvent.contentSize.height);
          }}
          onBlur={handleSave}
          multiline={true}
          maxLength={500}
          editable={isOwner}
        />
        <Text style={[profileStyles.characterCount, {
          alignSelf: 'flex-end',
          marginTop: 5,
          color: 'rgba(0, 0, 0, 0.5)',
          fontSize: 12
        }]}>
          {(orgBio?.length || 0)}/500
        </Text>
      </View>

      {/* Public Media Section */}
      <View style={{ width: '100%', marginTop: 20 }}>
        <Text style={[profileStyles.sectionTitle, { color: '#000000', marginBottom: 10 }]}>Organization Media</Text>
        
        {/* Media Display Grid - 3 slots */}
        <View style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 15
        }}>
          {/* Media Slot 1 */}
          <View style={{
            flex: 1,
            height: 80,
            borderRadius: 8,
            overflow: 'hidden',
            position: 'relative'
          }}>
            {orgMedia.media && orgMedia.media[0] ? (
              <TouchableOpacity 
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'relative'
                }}
              >
                {getFileType(orgMedia.media[0].name) === 'image' ? (
                  <Image
                    source={{ uri: orgMedia.media[0].url }}
                    style={{ width: '100%', height: '100%' }}
                    resizeMode="cover"
                  />
                ) : getFileType(orgMedia.media[0].name) === 'pdf' ? (
                  <View style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#e74c3c',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <Ionicons name="document-text" size={24} color="#ffffff" />
                    <Text style={{
                      color: '#ffffff',
                      fontSize: 8,
                      fontFamily: 'VastagoGrotesk',
                      marginTop: 2,
                      fontWeight: 'bold'
                    }}>PDF</Text>
                  </View>
                ) : (
                  <View style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#34495e',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <Ionicons name="attach" size={24} color="#ffffff" />
                    <Text style={{
                      color: '#ffffff',
                      fontSize: 8,
                      fontFamily: 'VastagoGrotesk',
                      marginTop: 2,
                      fontWeight: 'bold'
                    }}>FILE</Text>
                  </View>
                )}
                
                {/* Delete button - Only for owners */}
                {isOwner && (
                  <TouchableOpacity
                    onPress={() => handleOrgMediaDelete(orgMedia.media[0].id)}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255, 0, 0, 0.8)',
                      borderRadius: 10,
                      width: 20,
                      height: 20,
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Text style={{ color: '#ffffff', fontSize: 12, fontWeight: 'bold' }}>×</Text>
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            ) : (
              isOwner ? (
                <TouchableOpacity
                  onPress={() => handleOrgMediaUpload('media1')}
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: `${colors.purpleReplacement}20`,
                    borderRadius: 8,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderWidth: 2,
                    borderColor: `${colors.purpleReplacement}40`,
                    borderStyle: 'dashed'
                  }}
                >
                  <Ionicons 
                    name="add-circle" 
                    size={20} 
                    color={colors.purpleReplacement} 
                  />
                  <Text style={{
                    color: colors.purpleReplacement,
                    fontFamily: 'VastagoGrotesk',
                    fontSize: 10,
                    marginTop: 2,
                    textAlign: 'center'
                  }}>Add Media</Text>
                </TouchableOpacity>
              ) : (
                <View style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: 8,
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Text style={{
                    color: 'rgba(0, 0, 0, 0.3)',
                    fontFamily: 'VastagoGrotesk',
                    fontSize: 10,
                    textAlign: 'center'
                  }}>No Media</Text>
                </View>
              )
            )}
          </View>

          {/* Media Slot 2 */}
          <View style={{
            flex: 1,
            height: 80,
            borderRadius: 8,
            overflow: 'hidden',
            position: 'relative'
          }}>
            {orgMedia.media && orgMedia.media[1] ? (
              <TouchableOpacity 
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'relative'
                }}
              >
                {getFileType(orgMedia.media[1].name) === 'image' ? (
                  <Image
                    source={{ uri: orgMedia.media[1].url }}
                    style={{ width: '100%', height: '100%' }}
                    resizeMode="cover"
                  />
                ) : getFileType(orgMedia.media[1].name) === 'pdf' ? (
                  <View style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#e74c3c',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <Ionicons name="document-text" size={24} color="#ffffff" />
                    <Text style={{
                      color: '#ffffff',
                      fontSize: 8,
                      fontFamily: 'VastagoGrotesk',
                      marginTop: 2,
                      fontWeight: 'bold'
                    }}>PDF</Text>
                  </View>
                ) : (
                  <View style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#34495e',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <Ionicons name="attach" size={24} color="#ffffff" />
                    <Text style={{
                      color: '#ffffff',
                      fontSize: 8,
                      fontFamily: 'VastagoGrotesk',
                      marginTop: 2,
                      fontWeight: 'bold'
                    }}>FILE</Text>
                  </View>
                )}
                
                {/* Delete button - Only for owners */}
                {isOwner && (
                  <TouchableOpacity
                    onPress={() => handleOrgMediaDelete(orgMedia.media[1].id)}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255, 0, 0, 0.8)',
                      borderRadius: 10,
                      width: 20,
                      height: 20,
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Text style={{ color: '#ffffff', fontSize: 12, fontWeight: 'bold' }}>×</Text>
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            ) : (
              isOwner ? (
                <TouchableOpacity
                  onPress={() => handleOrgMediaUpload('media2')}
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: `${colors.purpleReplacement}20`,
                    borderRadius: 8,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderWidth: 2,
                    borderColor: `${colors.purpleReplacement}40`,
                    borderStyle: 'dashed'
                  }}
                >
                  <Ionicons 
                    name="add-circle" 
                    size={20} 
                    color={colors.purpleReplacement} 
                  />
                  <Text style={{
                    color: colors.purpleReplacement,
                    fontFamily: 'VastagoGrotesk',
                    fontSize: 10,
                    marginTop: 2,
                    textAlign: 'center'
                  }}>Add Media</Text>
                </TouchableOpacity>
              ) : (
                <View style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: 8,
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Text style={{
                    color: 'rgba(0, 0, 0, 0.3)',
                    fontFamily: 'VastagoGrotesk',
                    fontSize: 10,
                    textAlign: 'center'
                  }}>No Media</Text>
                </View>
              )
            )}
          </View>

          {/* Media Slot 3 */}
          <View style={{
            flex: 1,
            height: 80,
            borderRadius: 8,
            overflow: 'hidden',
            position: 'relative'
          }}>
            {orgMedia.media && orgMedia.media[2] ? (
              <TouchableOpacity 
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'relative'
                }}
              >
                {getFileType(orgMedia.media[2].name) === 'image' ? (
                  <Image
                    source={{ uri: orgMedia.media[2].url }}
                    style={{ width: '100%', height: '100%' }}
                    resizeMode="cover"
                  />
                ) : getFileType(orgMedia.media[2].name) === 'pdf' ? (
                  <View style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#e74c3c',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <Ionicons name="document-text" size={24} color="#ffffff" />
                    <Text style={{
                      color: '#ffffff',
                      fontSize: 8,
                      fontFamily: 'VastagoGrotesk',
                      marginTop: 2,
                      fontWeight: 'bold'
                    }}>PDF</Text>
                  </View>
                ) : (
                  <View style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#34495e',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}>
                    <Ionicons name="attach" size={24} color="#ffffff" />
                    <Text style={{
                      color: '#ffffff',
                      fontSize: 8,
                      fontFamily: 'VastagoGrotesk',
                      marginTop: 2,
                      fontWeight: 'bold'
                    }}>FILE</Text>
                  </View>
                )}
                
                {/* Delete button - Only for owners */}
                {isOwner && (
                  <TouchableOpacity
                    onPress={() => handleOrgMediaDelete(orgMedia.media[2].id)}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255, 0, 0, 0.8)',
                      borderRadius: 10,
                      width: 20,
                      height: 20,
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Text style={{ color: '#ffffff', fontSize: 12, fontWeight: 'bold' }}>×</Text>
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            ) : (
              isOwner ? (
                <TouchableOpacity
                  onPress={() => handleOrgMediaUpload('media3')}
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: `${colors.purpleReplacement}20`,
                    borderRadius: 8,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderWidth: 2,
                    borderColor: `${colors.purpleReplacement}40`,
                    borderStyle: 'dashed'
                  }}
                >
                  <Ionicons 
                    name="add-circle" 
                    size={20} 
                    color={colors.purpleReplacement} 
                  />
                  <Text style={{
                    color: colors.purpleReplacement,
                    fontFamily: 'VastagoGrotesk',
                    fontSize: 10,
                    marginTop: 2,
                    textAlign: 'center'
                  }}>Add Media</Text>
                </TouchableOpacity>
              ) : (
                <View style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: 8,
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Text style={{
                    color: 'rgba(0, 0, 0, 0.3)',
                    fontFamily: 'VastagoGrotesk',
                    fontSize: 10,
                    textAlign: 'center'
                  }}>No Media</Text>
                </View>
              )
            )}
          </View>
        </View>
      </View>

      {/* Members Section */}
      {isEditable && (
        <View style={{ width: '100%', marginTop: 20 }}>
          <Text style={[profileStyles.sectionTitle, { color: '#000000', marginBottom: 10 }]}>Members</Text>
          {members && members.length > 0 ? (
            <View>
              {members.map((memberId) => (
                <MemberListItem
                  key={memberId}
                  memberId={memberId}
                  org={{ id: orgId, ownerId: orgOwnerId, role: isOwner ? 'owner' : 'member' }}
                  userData={userData}
                  colors={colors}
                  onKickMember={handleKickMember}
                  onTransferOwnership={handleTransferOwnership}
                />
              ))}
            </View>
          ) : (
            <Text style={{ fontSize: 12, color: '#666' }}>No members</Text>
          )}

          {/* Invite System - Only for owners */}
          {isOwner && (
            <View style={{ marginTop: 15 }}>
              <Text style={[profileStyles.sectionTitle, { color: '#000000', marginBottom: 10 }]}>Invite Members</Text>
              <View style={{
                flexDirection: 'row',
                gap: 10,
                marginBottom: 10
              }}>
                <TextInput
                  style={[
                    profileStyles.profileInput,
                    {
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      color: '#000000',
                      height: 40,
                      paddingTop: 12,
                      paddingBottom: 12,
                      paddingLeft: 12,
                      borderRadius: 8,
                      flex: 1,
                      opacity: (!orgUsername.trim() || !orgTitle.trim()) ? 0.5 : 1
                    }
                  ]}
                  value={inviteEmailInput}
                  placeholder="Enter member email"
                  placeholderTextColor="rgba(0, 0, 0, 0.5)"
                  onChangeText={setInviteEmailInput}
                  keyboardType="email-address"
                  editable={orgUsername.trim() && orgTitle.trim()}
                />
                <TouchableOpacity
                  style={{
                    backgroundColor: (!orgUsername.trim() || !orgTitle.trim() || isInviting) ? '#ccc' : colors.purpleReplacement,
                    padding: 10,
                    borderRadius: 8,
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 100,
                    opacity: (!orgUsername.trim() || !orgTitle.trim() || isInviting) ? 0.5 : 1,
                    flexDirection: 'row',
                    gap: 8
                  }}
                  onPress={handleInviteFromCard}
                  disabled={!orgUsername.trim() || !orgTitle.trim() || isInviting}
                >
                  {isInviting && (
                    <ActivityIndicator size="small" color="#fff" />
                  )}
                  <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>
                    {isInviting ? 'Sending...' : 'Invite'}
                  </Text>
                </TouchableOpacity>
              </View>
              {(!orgUsername.trim() || !orgTitle.trim()) && (
                <Text style={{
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontSize: 12,
                  fontStyle: 'italic',
                  fontFamily: 'VastagoGrotesk',
                  textAlign: 'center',
                  marginTop: 5
                }}>
                  Please fill in username and title to enable invitations
                </Text>
              )}
            </View>
          )}
        </View>
      )}

      {/* Action Buttons */}
      <View style={{ marginTop: 20 }}>
        {/* Delete Organization Button - Only for owners */}
        {isOwner && (
          <TouchableOpacity
            style={[profileStyles.signOutButton, { 
              backgroundColor: '#e74c3c',
              marginBottom: onClose ? 10 : 0
            }]}
            onPress={handleDeleteOrganization}
          >
            <Text style={[profileStyles.signOutText, { color: '#fff' }]}>Delete Organization</Text>
          </TouchableOpacity>
        )}

   
      </View>

      {/* Success Modal */}
      <Modal
        visible={showSuccessModal !== null}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowSuccessModal(null)}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}>
          <View style={{
            backgroundColor: '#ffffff',
            borderRadius: 12,
            padding: 20,
            width: '100%',
            maxWidth: 400,
            alignItems: 'center',
          }}>
            <Ionicons name="checkmark-circle" size={48} color="#2ecc71" />
            <Text style={{
              fontSize: 18,
              fontFamily: 'Glacial Indifference',
              marginTop: 10,
              marginBottom: 15,
              textAlign: 'center',
              color: '#2ecc71',
            }}>Success!</Text>

            <Text style={{
              fontSize: 14,
              color: '#333',
              textAlign: 'center',
              marginBottom: 20,
              lineHeight: 20,
            }}>{showSuccessModal}</Text>

            <TouchableOpacity
              onPress={() => setShowSuccessModal(null)}
              style={{
                backgroundColor: '#2ecc71',
                borderRadius: 8,
                paddingHorizontal: 30,
                paddingVertical: 12,
                alignItems: 'center',
              }}
            >
              <Text style={{
                color: '#ffffff',
                fontSize: 16,
                fontFamily: 'Glacial Indifference',
                fontWeight: 'bold',
              }}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Error Modal */}
      <Modal
        visible={showErrorModal !== null}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowErrorModal(null)}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}>
          <View style={{
            backgroundColor: '#ffffff',
            borderRadius: 12,
            padding: 20,
            width: '100%',
            maxWidth: 400,
            alignItems: 'center',
          }}>
            <Ionicons name="alert-circle" size={48} color="#e74c3c" />
            <Text style={{
              fontSize: 18,
              fontFamily: 'Glacial Indifference',
              marginTop: 10,
              marginBottom: 15,
              textAlign: 'center',
              color: '#e74c3c',
            }}>Error</Text>

            <Text style={{
              fontSize: 14,
              color: '#333',
              textAlign: 'center',
              marginBottom: 20,
              lineHeight: 20,
            }}>{showErrorModal}</Text>

            <TouchableOpacity
              onPress={() => setShowErrorModal(null)}
              style={{
                backgroundColor: '#e74c3c',
                borderRadius: 8,
                paddingHorizontal: 30,
                paddingVertical: 12,
                alignItems: 'center',
              }}
            >
              <Text style={{
                color: '#ffffff',
                fontSize: 16,
                fontFamily: 'Glacial Indifference',
                fontWeight: 'bold',
              }}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

